import React, { useState, useEffect } from 'react';
import { Trophy, List, Users, Waves, Award } from 'lucide-react';
import { Avatar } from '../../components/UI';
import { analyticsApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { LeaderboardEntry } from '../../types';
import styles from './Leaderboard.module.css';

const Leaderboard: React.FC = () => {
  const { user } = useAuthStore();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [totals, setTotals] = useState({ total_ripples: 0, people_recognized: 0, waves_distributed: 0 });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [type, setType] = useState<'received' | 'given'>('received');

  useEffect(() => {
    fetchLeaderboard();
  }, [period, type]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await analyticsApi.getLeaderboard({ period, type });
      const data = response.data;
      if (data.entries) {
        setEntries(data.entries);
        setTotals(data.totals || { total_ripples: 0, people_recognized: 0, waves_distributed: 0 });
      } else {
        setEntries(data);
        setTotals({ total_ripples: 0, people_recognized: 0, waves_distributed: 0 });
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPoints = (entry: LeaderboardEntry) =>
    Number(type === 'received' ? entry.points_received : entry.points_given) || 0;

  const getRipples = (entry: LeaderboardEntry) =>
    Number(type === 'received' ? entry.appreciations_received : entry.appreciations_given) || 0;

  const activeEntries = entries.filter(e => getPoints(e) > 0 || getRipples(e) > 0);

  const maxWaves = activeEntries.length > 0 ? Math.max(...activeEntries.map(getPoints), 1) : 1;

  const podiumOrder = [1, 0, 2];
  const rankColors = ['#EF9F27', '#B4B2A9', '#D85A30'];

  return (
    <div className={styles.container}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <h1 className={styles.pageTitle}>Ripple Leaders</h1>
        </div>
        <div className={styles.pageHeaderRight}>
          <div className={styles.periodTabs}>
            {(['week', 'month', 'quarter', 'year'] as const).map((p) => (
              <button
                key={p}
                className={`${styles.periodTab} ${period === p ? styles.periodTabActive : ''}`}
                onClick={() => setPeriod(p)}
              >
                {p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : p === 'quarter' ? 'Quarter' : 'Year'}
              </button>
            ))}
          </div>
          <div className={styles.modeToggle}>
            <button
              className={`${styles.modeBtn} ${type === 'received' ? styles.modeBtnActive : ''}`}
              onClick={() => setType('received')}
            >
              Most Recognized
            </button>
            <button
              className={`${styles.modeBtn} ${type === 'given' ? styles.modeBtnActive : ''}`}
              onClick={() => setType('given')}
            >
              Top Givers
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Loading leaderboard...</span>
        </div>
      ) : activeEntries.length === 0 ? (
        <div className={styles.empty}>
          <Trophy size={48} />
          <h3>No data yet</h3>
          <p>Start sending ripples to see the leaderboard!</p>
        </div>
      ) : (
        <>
          {/* Podium Section */}
          <div className={styles.podiumCard}>
            <div className={styles.podiumHero}>
              <div className={styles.podiumItems}>
                {podiumOrder.map((idx, pos) => {
                  const person = activeEntries[idx];
                  if (!person) return <div key={pos} className={styles.podiumPlaceholder} />;
                  const isCenter = idx === 0;
                  const rankIdx = person.rank - 1;
                  return (
                    <div
                      key={person.id}
                      className={`${styles.podiumItem} ${isCenter ? styles.podiumItemCenter : ''}`}
                    >
                      <div className={styles.podiumAvatarWrap}>
                        <Avatar
                          src={person.avatar_url}
                          name={`${person.first_name} ${person.last_name}`}
                          size={isCenter ? 'xl' : 'lg'}
                        />
                        <div
                          className={styles.podiumRankBadge}
                          style={{ background: rankColors[rankIdx] || '#999' }}
                        >
                          {person.rank}
                        </div>
                      </div>
                      <div className={styles.podiumName}>
                        {person.first_name} {person.last_name}
                      </div>
                      <div className={styles.podiumWavesValue}>
                        {getPoints(person)}
                      </div>
                      <div className={styles.podiumWavesUnit}>waves</div>
                      <div
                        className={styles.podiumBlock}
                        style={{ height: isCenter ? 80 : person.rank === 2 ? 56 : 40 }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Stats Bar */}
            <div className={styles.statsBar}>
              <div className={styles.statItem}>
                <div className={styles.statItemIcon} style={{ background: '#F0FDFA' }}>
                  <Waves size={16} color="#0C6B62" />
                </div>
                <div className={styles.statItemInfo}>
                  <span className={styles.statItemValue}>{totals.total_ripples}</span>
                  <span className={styles.statItemLabel}>Total Ripples</span>
                </div>
              </div>
              <div className={styles.statItem}>
                <div className={styles.statItemIcon} style={{ background: '#EEF2FF' }}>
                  <Users size={16} color="#6366F1" />
                </div>
                <div className={styles.statItemInfo}>
                  <span className={styles.statItemValue}>{totals.people_recognized}</span>
                  <span className={styles.statItemLabel}>People Recognized</span>
                </div>
              </div>
              <div className={styles.statItem}>
                <div className={styles.statItemIcon} style={{ background: '#FFFBEB' }}>
                  <Award size={16} color="#D97706" />
                </div>
                <div className={styles.statItemInfo}>
                  <span className={styles.statItemValue}>{totals.waves_distributed}</span>
                  <span className={styles.statItemLabel}>Waves Distributed</span>
                </div>
              </div>
            </div>
          </div>

          {/* Full Table */}
          <div className={styles.tableCard}>
            <div className={styles.tableHeader}>
              <span className={styles.thRank}>Rank</span>
              <span className={styles.thUser}>User</span>
              <span className={styles.thRipples}>Ripples</span>
              <span className={styles.thWaves}>Waves</span>
            </div>
            <div className={styles.tableBody}>
              {activeEntries.map((entry) => {
                const isTop3 = entry.rank <= 3;
                const isCurrentUser = user?.id === entry.id;
                const borderColor = isTop3 ? rankColors[entry.rank - 1] : undefined;
                const wavePct = Math.max(8, (getPoints(entry) / maxWaves) * 100);

                return (
                  <div
                    key={entry.id}
                    className={`${styles.tableRow} ${isCurrentUser ? styles.tableRowYou : ''}`}
                    style={borderColor ? { borderLeft: `3px solid ${borderColor}` } : undefined}
                  >
                    <div className={styles.tdRank}>
                      {isTop3 ? (
                        <span className={styles.rankTrophy}>
                          {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : '🥉'}
                        </span>
                      ) : (
                        <span className={styles.rankNum}>#{entry.rank}</span>
                      )}
                    </div>
                    <div className={styles.tdUser}>
                      <Avatar
                        src={entry.avatar_url}
                        name={`${entry.first_name} ${entry.last_name}`}
                        size="md"
                      />
                      <span className={styles.tdUserName}>
                        {entry.first_name} {entry.last_name}
                      </span>
                      {isCurrentUser && <span className={styles.youBadge}>You</span>}
                    </div>
                    <div className={styles.tdRipples}>
                      {getRipples(entry)}
                    </div>
                    <div className={styles.tdWaves}>
                      <span className={styles.tdWavesValue}>{getPoints(entry)}</span>
                      <div className={styles.tdWavesBarTrack}>
                        <div
                          className={styles.tdWavesBarFill}
                          style={{ width: `${wavePct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Leaderboard;
